import queryString from 'query-string';

// Dangerous property names that could lead to prototype pollution
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Checks if a key is safe to use as an object property
 * to prevent prototype pollution attacks
 *
 * Validates that the key:
 * 1. Is not in the dangerous keys set
 * 2. Contains only alphanumeric characters, hyphens, underscores, and dots
 * 3. Does not start with a dot or special character
 */
function isSafeKey(key: string): boolean {
  // Check against dangerous keys
  if (DANGEROUS_KEYS.has(key)) {
    return false;
  }

  // Validate key format: only allow alphanumeric, hyphens, underscores, and dots
  // This prevents injection of special property names
  const safeKeyPattern = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/;
  if (!safeKeyPattern.test(key)) {
    return false;
  }

  return true;
}

export function createQueryParamsFromLocation(): Record<
  string,
  string | undefined
> {
  const parsedParams = queryString.parse(location.search);

  // Use Map to store parameters - this prevents prototype pollution attacks
  // as Maps don't have a prototype chain that can be polluted
  const paramsMap = new Map<string, string>();

  for (const [key, value] of Object.entries(parsedParams)) {
    // Skip dangerous keys as an additional layer of defense
    if (!isSafeKey(key)) {
      continue;
    }

    if (Array.isArray(value)) {
      paramsMap.set(key, value[0]!);
    } else if (value) {
      paramsMap.set(key, value);
    }
  }

  // Convert Map to plain object for return type compatibility
  // Using Object.create(null) ensures no prototype chain
  // Using Object.defineProperty explicitly sets properties, which CodeQL recognizes as safer
  const params = Object.create(null) as Record<string, string>;
  for (const [key, value] of paramsMap) {
    // Use Object.defineProperty to explicitly define the property
    // This prevents prototype pollution and is recognized by CodeQL as safe
    Object.defineProperty(params, key, {
      value,
      writable: true,
      enumerable: true,
      configurable: true,
    });
  }

  return params;
}
